import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const EngagementContext = createContext(null);
export const ACTIVE_KEY = "csx_active_engagement";

export const EngagementProvider = ({ children }) => {
  const [engagements, setEngagements] = useState([]);
  // Active engagement ID is a UI preference, not a credential — safe in localStorage.
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(ACTIVE_KEY) || "demo-default");
  const [showDemo, setShowDemo] = useState(() => localStorage.getItem("csx_show_demo") !== "false");

  const setActiveId = useCallback((id) => {
    localStorage.setItem(ACTIVE_KEY, id);
    setActiveIdState(id);
    // hard reload so all open pages re-fetch with new engagement header
    window.location.reload();
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/engagements");
      setEngagements(data);
    } catch (err) {
      // 401 is handled by the axios interceptor (redirects to login).
      // Log everything else so silent failures don't disappear.
      if (err?.response?.status !== 401) {
        console.warn("[engagements] refresh failed:", err);
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleShowDemo = useCallback(() => {
    const next = !showDemo;
    localStorage.setItem("csx_show_demo", next ? "true" : "false");
    setShowDemo(next);
  }, [showDemo]);

  const active = useMemo(
    () => engagements.find((e) => e.id === activeId),
    [engagements, activeId],
  );

  // Memoize the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ engagements, activeId, active, setActiveId, refresh, showDemo, toggleShowDemo }),
    [engagements, activeId, active, setActiveId, refresh, showDemo, toggleShowDemo],
  );

  return (
    <EngagementContext.Provider value={value}>
      {children}
    </EngagementContext.Provider>
  );
};

export const useEngagement = () => useContext(EngagementContext) || {};
